/**
 * HomeBaseNudge — one-time dashboard prompt for owners who signed up
 * before the "home base" field existed (Aug 2026). Inline save, dismissible
 * forever, disappears automatically once a home base is on file.
 */
import { useState } from "react";
import { MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import api from "../lib/api";

const DISMISS_KEY = "tt_homebase_nudge_dismissed";

export const HomeBaseNudge = () => {
  const { user, refresh } = useAuth();
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });
  const [saved, setSaved] = useState(false);

  if (!user || user.role !== "owner" || user.home_base || dismissed || saved) return null;

  const save = async (e) => {
    e.preventDefault();
    if (value.trim().length < 2) return;
    setBusy(true);
    try {
      await api.post("/auth/me/home-base", { home_base: value.trim() });
      setSaved(true);
      toast.success("Home base saved");
      if (refresh) refresh();
    } catch {
      toast.error("Couldn't save — try again");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
    setDismissed(true);
  };

  return (
    <div
      className="mt-4 rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
      data-testid="homebase-nudge"
    >
      <div className="flex items-center gap-2 text-sm font-medium flex-none">
        <MapPin weight="duotone" className="h-4 w-4 text-primary" />
        Where's your truck's home base?
      </div>
      <form onSubmit={save} className="flex flex-1 gap-2">
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          maxLength={120}
          placeholder="City, State — e.g. Lancaster, PA"
          className="flex-1 h-9 rounded-full border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          data-testid="homebase-nudge-input"
        />
        <button
          type="submit"
          disabled={busy || value.trim().length < 2}
          className="h-9 px-4 rounded-full bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50 hover:opacity-90 transition-opacity"
          data-testid="homebase-nudge-save"
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </form>
      <button
        type="button"
        onClick={dismiss}
        className="text-xs text-muted-foreground hover:text-foreground transition-colors flex-none"
        data-testid="homebase-nudge-dismiss"
      >
        Not now
      </button>
    </div>
  );
};

export default HomeBaseNudge;
